"use client";

import type { ContactSubject } from "@graphql";
import { Select, cn } from "@tosize/ui";
import { usePathname } from "next/navigation";
import { RadioGroup } from "radix-ui";
import type { ChangeEvent } from "react";
import { useFormContext } from "react-hook-form";
import { useQuery } from "~/hooks/useQuery";
import { useRouter } from "~/hooks/useRouter";
import { TicketType } from "../types";

interface SubjectSelectorProps {
	subjects: ContactSubject[];
	errorMessage: string;
	emptyOptionMessage: string;
}

export function SubjectSelector({
	subjects,
	errorMessage,
	emptyOptionMessage,
}: SubjectSelectorProps) {
	const pathname = usePathname();
	const router = useRouter();
	const { createUrl } = useQuery();

	const { register, watch, setValue } = useFormContext();
	const type = watch("type");

	const onValueChange = (value: string) => {
		setValue("type", value);

		if (value === TicketType.CHANGE) {
			router.push("/order/edit");
		}

		if (pathname?.startsWith("/contacts") && value !== TicketType.CHANGE) {
			router.replace(createUrl({ type: value }));
		}
	};
	return (
		<>
			<Select
				{...register("type", {
					required: errorMessage,
					onChange: (e: ChangeEvent<HTMLInputElement>) => onValueChange(e.target.value),
				})}
				className="my-2 xs:my-4 xs:hidden w-full"
			>
				<option value="">{emptyOptionMessage}</option>
				{subjects.map((subject, key) => (
					<option key={key} value={subject.category}>
						{subject.label}
					</option>
				))}
			</Select>

			<div className="xs:block hidden">
				<input type="hidden" {...register("type")} />
				<RadioGroup.Root
					className="my-2 xs:my-4 grid grid-cols-1 xs:grid-cols-2 gap-4 sm:grid-cols-3"
					value={type}
					onValueChange={onValueChange}
				>
					{subjects.map((subject, index) => {
						if (subject.category) {
							return (
								<RadioGroup.Item
									key={index}
									value={subject.category}
									title={subject.label}
									className={cn(
										"p-2 sm:p-4",
										"flex items-center justify-center rounded border border-shades text-primary-medium leading-snug hover:border-primary-medium focus:outline-none motion-safe:transition-colors",
										"rdx-state-checked:border-primary-medium rdx-state-checked:ring-1 rdx-state-checked:ring-primary-medium rdx-state-checked:ring-inset rdx-state-checked:ring-offset-1",
									)}
								>
									{subject.label}
								</RadioGroup.Item>
							);
						}
					})}
				</RadioGroup.Root>
			</div>
		</>
	);
}
